import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { CLIENTS, getClient } from "@/data/site";

const ProjectDetail = () => {
  const { slug } = useParams();
  const client = getClient(slug);

  useEffect(() => {
    document.title = client ? `${client.name} — Work — Click World Online` : "Work — Click World Online";
  }, [client]);

  if (!client) {
    return (
      <section className="bg-navy-deep min-h-screen flex items-center">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-40 text-center w-full">
          <h1 className="font-display text-4xl md:text-6xl font-extrabold text-mist tracking-tight">Project not found.</h1>
          <Link to="/portfolio" data-testid="project-not-found-back" className="mt-8 inline-flex items-center gap-2 text-electric font-semibold">
            Back to our work <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    );
  }

  const idx = CLIENTS.findIndex((c) => c.slug === slug);
  const next = CLIENTS[(idx + 1) % CLIENTS.length];

  return (
    <>
      <PageHeader eyebrow={client.category} title={client.name} text={client.blurb} breadcrumb={[{ label: "Home", to: "/" }, { label: "Our Work", to: "/portfolio" }, { label: client.name }]} />

      <section className="bg-navy-deep" data-testid="project-overview-section">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <h2 className="font-display text-2xl lg:text-3xl font-extrabold text-mist tracking-tight">The engagement</h2>
            <Reveal delay={0.08}>
              <p className="mt-6 text-slatec text-base md:text-lg leading-relaxed max-w-2xl">{client.blurb}</p>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="mt-6 text-slatec leading-relaxed max-w-2xl">
                Like every Click World Online engagement, this partnership runs through our four-stage process —
                Discover, Strategize, Create, Launch &amp; Optimize — so the work stays connected to real business goals.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 bg-navy-light border border-white/10 rounded-2xl p-6 max-w-2xl">
                <p className="text-sm text-slatec leading-relaxed">
                  Detailed documentation, media and verified outcomes for this engagement are shared on request —{" "}
                  <Link to="/contact" data-testid="project-request-details" className="text-electric hover:underline">
                    ask us directly
                  </Link>{" "}
                  and we'll walk you through the work.
                </p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="bg-navy-light border border-white/10 rounded-3xl p-8 lg:p-10 lg:sticky lg:top-28 grain relative overflow-hidden">
                <div className="absolute inset-0 dot-grid opacity-15" aria-hidden="true" />
                <div className="relative">
                  {client.location && (
                    <p className="mb-6 flex items-center gap-2 text-sm text-slatec" data-testid="project-location">
                      <span className="w-1.5 h-1.5 rounded-full bg-electric" aria-hidden="true" />
                      {client.location}
                    </p>
                  )}
                  {client.services.length > 0 && (
                    <>
                      <h3 className="font-display text-xl font-extrabold text-mist tracking-tight">Services involved</h3>
                      <div className="mt-6 flex flex-wrap gap-2">
                        {client.services.map((s) => (
                          <span key={s} className="text-sm text-mist/80 border border-white/10 bg-white/5 rounded-full px-4 py-2">
                            {s}
                          </span>
                        ))}
                      </div>
                    </>
                  )}
                  {client.link && (
                    <a
                      href={client.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="project-external-link"
                      className="mt-6 inline-flex items-center gap-2 text-electric text-sm font-semibold hover:gap-3 transition-[gap] duration-300"
                    >
                      Visit {client.name} on Instagram <ArrowUpRight size={16} />
                    </a>
                  )}
                  {client.slug === "berkowits" && (
                    <Link
                      to="/360-experiences"
                      data-testid="project-360-link"
                      className="mt-6 inline-flex items-center gap-2 text-electric text-sm font-semibold hover:gap-3 transition-[gap] duration-300"
                    >
                      See our 360° tour work <ArrowUpRight size={16} />
                    </Link>
                  )}
                  <Link
                    to="/contact"
                    data-testid="project-cta"
                    className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-electric hover:text-navy-deep text-white font-semibold px-8 py-4 rounded-full transition-colors duration-300"
                  >
                    Start your project <ArrowUpRight size={18} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-navy border-t border-white/5">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-14 flex items-center justify-between gap-6">
          <Link to="/portfolio" data-testid="project-back-link" className="inline-flex items-center gap-2 text-slatec hover:text-electric transition-colors text-sm font-semibold">
            <ArrowLeft size={16} /> All work
          </Link>
          <Link to={`/portfolio/${next.slug}`} data-testid="project-next-link" className="group inline-flex items-center gap-3 text-right">
            <span>
              <span className="block text-xs uppercase tracking-[0.2em] text-mist/40">Next project</span>
              <span className="block font-display text-lg lg:text-2xl font-extrabold text-mist group-hover:text-electric transition-colors duration-300">{next.name}</span>
            </span>
            <ArrowRight size={20} className="text-electric group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default ProjectDetail;
